<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Toolbar,
		Button,
		IconButton,
		Text,
		DropdownMenu,
		DropdownMenuItem,
	} from '@Shelly/shelly-design-system';
	import { flowCanvasTheme } from '../../../stores/flow_editor';

	export let flowName: string;
	export let saveState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
	export let saveStateMessage = '';
	export let loadStatusMessage = '';
	export let isSaving = false;
	export let canUndo = false;
	export let canRedo = false;
	export let isRunning = false;
	export let canRun = false;
	export let isTestingFlow = false;

	export let onCancel: () => void;
	export let onSave: () => void;
	export let onAddNode: (selection?: 'all' | 'when' | 'if' | 'do' | 'note') => void;
	export let onClear: () => void;
	export let onSettings: () => void;
	export let onValidation: () => void;
	export let onHistory: () => void;
	export let onShortcuts: () => void;
	export let onUndo: (() => void) | undefined = undefined;
	export let onRedo: (() => void) | undefined = undefined;
	export let onLayout: (() => void) | undefined = undefined;
	export let onRun: (() => void) | undefined = undefined;
	export let onTestFlow: (() => void) | undefined = undefined;
	export let onViewJson: (() => void) | undefined = undefined;
	export let onTemplates: (() => void) | undefined = undefined;
	export let onSaveSubflow: (() => void) | undefined = undefined;
	export let onOutline: (() => void) | undefined = undefined;
	export let onExport: (() => void) | undefined = undefined;
	export let onImport: (() => void) | undefined = undefined;

	let showMoreMenu = false;
	let showAddMenu = false;

	$: statusLabel =
		saveState === 'saving'
			? 'Saving…'
			: saveState === 'error'
				? saveStateMessage || 'Save failed'
				: saveState === 'saved'
					? 'Saved'
					: '';
	$: inlineStatus = statusLabel || loadStatusMessage;

	function menuAction(action: () => void) {
		showMoreMenu = false;
		action();
	}

	function addMenuAction(selection: 'all' | 'when' | 'if' | 'do' | 'note') {
		showAddMenu = false;
		onAddNode(selection);
	}

	function toggleCanvasTheme() {
		$flowCanvasTheme = $flowCanvasTheme === 'dark' ? 'light' : 'dark';
	}
</script>

<Toolbar noBreadcrumbs isSticky useNewLayout class="flow-editor-toolbar">
	<div slot="leftSide" class="toolbar-main">
		<div class="toolbar-identity">
			<IconButton
				variant="secondary"
				icon="fas fa-arrow-left"
				aria-label={$format('label_back', { default: 'Back to flows' })}
				on:click={onCancel}
			/>
			<div class="flow-title-group">
				<Text variant="title" size="m" class="flow-title">
					{flowName || $format('label_new_flow', { default: 'New Flow' })}
				</Text>
				{#if inlineStatus}
					<span
						class="save-status status-{statusLabel ? saveState : 'info'}"
						role="status"
						aria-live="polite"
						title={inlineStatus}
					>
						<i
							class="fas {statusLabel
								? saveState === 'saved'
									? 'fa-circle-check'
									: saveState === 'error'
										? 'fa-circle-exclamation'
										: 'fa-spinner fa-spin'
								: 'fa-circle-info'}"
							aria-hidden="true"
						></i>
						{inlineStatus}
					</span>
				{/if}
			</div>
		</div>

		<div class="toolbar-create-edit">
			<DropdownMenu bind:show={showAddMenu}>
				<Button
					slot="trigger"
					variant="primary"
					size="m"
					icon="fas fa-plus"
					aria-label="Add"
					aria-expanded={showAddMenu}
					on:click={() => (showAddMenu = !showAddMenu)}
				>
					<span class="toolbar-button-label add-button-label">Add</span>
				</Button>
				<DropdownMenuItem
					title="All cards"
					icon="fas fa-grid-2"
					on:click={() => addMenuAction('all')}
				/>
				<div class="add-menu-separator" role="separator"></div>
				<DropdownMenuItem
					title="When"
					icon="fas fa-bolt"
					on:click={() => addMenuAction('when')}
				/>
				<DropdownMenuItem
					title="If / And"
					icon="fas fa-circle-question"
					on:click={() => addMenuAction('if')}
				/>
				<DropdownMenuItem title="Do" icon="fas fa-play" on:click={() => addMenuAction('do')} />
				<div class="add-menu-separator" role="separator"></div>
				<DropdownMenuItem
					title="Note"
					icon="fas fa-note-sticky"
					on:click={() => addMenuAction('note')}
				/>
			</DropdownMenu>
			{#if onTemplates}
				<IconButton
					variant="secondary"
					icon="fas fa-layer-group"
					title="Flow templates"
					aria-label="Flow templates"
					on:click={onTemplates}
				/>
			{/if}
			<div class="history-actions toolbar-optional" role="group" aria-label="Edit history">
				<IconButton
					variant="secondary"
					icon="fas fa-undo"
					aria-label="Undo"
					disabled={!canUndo}
					on:click={onUndo}
				/>
				<IconButton
					variant="secondary"
					icon="fas fa-redo"
					aria-label="Redo"
					disabled={!canRedo}
					on:click={onRedo}
				/>
			</div>
			{#if onLayout}
				<IconButton
					variant="secondary"
					icon="fas fa-sitemap"
					title="Auto-arrange nodes"
					aria-label="Auto-arrange nodes"
					on:click={onLayout}
				/>
			{/if}
		</div>
	</div>

	<div slot="rightSide" class="toolbar-actions">
		<div class="flow-settings-actions" role="group" aria-label="Flow settings and validation">
			<Button
				variant="secondary"
				size="m"
				icon="fas fa-sliders"
				title="Flow settings"
				on:click={onSettings}
			>
				<span class="toolbar-button-label">Flow settings</span>
			</Button>
			<IconButton
				class="mobile-overflow-action"
				variant="secondary"
				icon="fas fa-circle-check"
				title="Validate flow"
				aria-label="Validate flow"
				on:click={onValidation}
			/>
		</div>
		{#if onTestFlow}
			<IconButton
				variant="secondary"
				icon={isTestingFlow ? 'fas fa-spinner fa-spin' : 'fas fa-route'}
				disabled={isTestingFlow}
				title={isTestingFlow ? 'Testing flow…' : 'Test flow'}
				aria-label="Test flow and preview its path"
				on:click={onTestFlow}
			/>
		{/if}
		{#if onRun}
			<Button
				variant="success"
				size="m"
				icon={isRunning ? 'fas fa-spinner fa-spin' : 'fas fa-play'}
				disabled={!canRun || isRunning}
				aria-label={$format('run_scene', { default: 'Run flow' })}
				on:click={onRun}
			>
				<span class="toolbar-button-label">{isRunning ? 'Running…' : 'Run'}</span>
			</Button>
		{/if}
		<Button variant="primary" size="m" icon="fas fa-save" disabled={isSaving} on:click={onSave}>
			<span class="toolbar-button-label">
				{isSaving ? 'Saving…' : $format('label_save', { default: 'Save' })}
			</span>
		</Button>

		<DropdownMenu bind:show={showMoreMenu}>
			<IconButton
				slot="trigger"
				variant="secondary"
				icon="fas fa-ellipsis-vertical"
				isActive={showMoreMenu}
				aria-label="More flow actions"
				aria-expanded={showMoreMenu}
				on:click={() => (showMoreMenu = !showMoreMenu)}
			/>
			<DropdownMenuItem
				title="Flow settings"
				icon="fas fa-gear"
				on:click={() => menuAction(onSettings)}
			/>
			<DropdownMenuItem
				title={$flowCanvasTheme === 'dark' ? 'Use light canvas' : 'Use dark canvas'}
				icon={$flowCanvasTheme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'}
				on:click={() => menuAction(toggleCanvasTheme)}
			/>
			{#if onViewJson}
				<DropdownMenuItem
					title="View flow JSON"
					icon="fas fa-code"
					on:click={() => menuAction(onViewJson)}
				/>
			{/if}
			{#if onTemplates}
				<DropdownMenuItem
					title="Templates"
					icon="fas fa-layer-group"
					on:click={() => menuAction(onTemplates)}
				/>
			{/if}
			{#if onSaveSubflow}
				<DropdownMenuItem
					title="Save selection as reusable subflow"
					icon="fas fa-puzzle-piece"
					on:click={() => menuAction(onSaveSubflow)}
				/>
			{/if}
			{#if onOutline}
				<DropdownMenuItem
					title="Outline · Experimental"
					icon="fas fa-list-tree"
					on:click={() => menuAction(onOutline)}
				/>
			{/if}
			<DropdownMenuItem
				title="Auto-arrange nodes"
				icon="fas fa-sitemap"
				on:click={() => onLayout && menuAction(onLayout)}
			/>
			{#if onExport}
				<DropdownMenuItem
					title="Export Flow token"
					icon="fas fa-copy"
					on:click={() => menuAction(onExport)}
				/>
			{/if}
			{#if onImport}
				<DropdownMenuItem
					title="Import Flow token"
					icon="fas fa-file-import"
					on:click={() => menuAction(onImport)}
				/>
			{/if}
			<DropdownMenuItem
				title="Validation"
				icon="fas fa-circle-check"
				on:click={() => menuAction(onValidation)}
			/>
			<DropdownMenuItem
				title="Test history"
				icon="fas fa-clock-rotate-left"
				on:click={() => menuAction(onHistory)}
			/>
			<DropdownMenuItem
				title="Keyboard shortcuts"
				icon="fas fa-keyboard"
				on:click={() => menuAction(onShortcuts)}
			/>
			<DropdownMenuItem
				title="Clear canvas"
				icon="fas fa-trash"
				titleClasses="text-danger"
				on:click={() => menuAction(onClear)}
			/>
		</DropdownMenu>
	</div>
</Toolbar>

<style>
	.toolbar-main,
	.toolbar-identity,
	.toolbar-create-edit,
	.toolbar-actions,
	.flow-settings-actions {
		display: flex;
		align-items: center;
	}

	.toolbar-main {
		gap: 20px;
		min-width: 0;
		max-width: 100%;
	}
	.toolbar-identity {
		gap: 10px;
		min-width: 0;
	}
	.toolbar-create-edit,
	.toolbar-actions {
		gap: 8px;
	}

	.toolbar-create-edit,
	.flow-settings-actions,
	.toolbar-actions > :global(button),
	.toolbar-actions > :global(*) {
		flex-shrink: 0;
	}

	.flow-settings-actions {
		gap: 4px;
		padding-right: 8px;
		border-right: 1px solid var(--color-3, rgba(148, 163, 184, 0.24));
	}

	.history-actions {
		display: flex;
		align-items: center;
		gap: 2px;
		padding: 2px;
		border: 1px solid var(--color-3, rgba(148, 163, 184, 0.24));
		border-radius: var(--radius-medium, 8px);
		background: var(--color-1, rgba(148, 163, 184, 0.08));
	}
	.add-menu-separator {
		height: 1px;
		margin: 4px 8px;
		background: var(--color-3, rgba(148, 163, 184, 0.22));
	}

	.flow-title-group {
		min-width: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 2px;
	}

	:global(.flow-title) {
		max-width: 260px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.save-status {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		max-width: 260px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		font-size: 12px;
		color: #9ca3af;
	}

	.save-status i {
		flex: 0 0 auto;
	}

	.status-saved {
		color: #4ade80;
	}
	.status-error {
		color: #f87171;
	}
	.status-info {
		color: #93c5fd;
	}

	@media (max-width: 1450px) {
		.toolbar-main {
			gap: 10px;
		}
		:global(.flow-title) {
			max-width: 180px;
		}
		.save-status {
			max-width: 180px;
		}
		.toolbar-optional {
			display: none;
		}
		.toolbar-create-edit > :global(button:first-child),
		.flow-settings-actions :global(button:first-child),
		.toolbar-actions > :global(button) {
			min-width: 40px;
		}
		.toolbar-button-label {
			display: none;
		}
	}

	@media (max-width: 1100px) {
		:global(.flow-editor-toolbar) {
			grid-template-columns: minmax(0, 1fr) !important;
			grid-template-areas:
				'left-controls'
				'right-controls' !important;
			grid-template-rows: auto auto !important;
		}

		:global(.flow-editor-toolbar .left-controls-container),
		:global(.flow-editor-toolbar .right-controls-container) {
			width: 100% !important;
			min-width: 0 !important;
		}
		:global(.flow-editor-toolbar .left-controls-container) {
			overflow: visible !important;
		}

		.toolbar-main,
		.toolbar-actions {
			width: 100%;
		}

		.toolbar-main {
			justify-content: space-between;
		}

		.toolbar-actions {
			justify-content: flex-start;
			overflow-x: auto;
			overflow-y: hidden;
			gap: 4px;
			padding-bottom: 2px;
			scrollbar-width: none;
		}

		.toolbar-actions::-webkit-scrollbar {
			display: none;
		}
	}

	@media (max-width: 639px) {
		.toolbar-main {
			flex-direction: row;
			align-items: center;
			gap: 4px;
		}

		.toolbar-identity {
			flex: 1;
			justify-content: flex-start;
			min-width: 0;
		}
		.flow-title-group {
			flex: 1;
		}
		:global(.flow-title) {
			max-width: 145px;
		}

		.save-status {
			max-width: 120px;
			font-size: 0;
		}
		.save-status i {
			font-size: 12px;
		}

		.toolbar-create-edit {
			display: flex;
			flex: 0 0 auto;
			gap: 4px;
		}
		.toolbar-create-edit > :global(button:first-child) {
			min-width: 40px;
			font-size: 0;
		}

		.toolbar-actions {
			max-width: 100%;
		}
		.toolbar-actions :global(button) {
			min-width: 40px;
		}
		.toolbar-actions > :global(button) {
			font-size: 0;
		}

		.flow-settings-actions {
			flex: 0 0 auto;
			gap: 4px;
			padding-right: 4px;
		}

		:global(.mobile-overflow-action) {
			display: none !important;
		}
	}
	.toolbar-button-label.add-button-label {
		display: inline;
		font-size: 12px;
	}
</style>
